import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  ArrowRight, 
  BookOpen, 
  Scroll, 
  Settings, 
  Home,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Heart,
  Lock,
  AlertCircle,
  Wallet
} from 'lucide-react';
import { chapterService, donationService } from '../services/firebase';
import Navbar from './Navbar';
import Footer from './Footer';
import './ChapterReader.css';

type ViewMode = 'scroll' | 'flip';

interface ChapterData {
  [key: number]: {
    title: string;
    content: string[];
  };
}

interface FormattedDonor {
  name: string;
  amount: number;
  date: string;
}

interface ChapterReaderProps {
  className?: string;
}

const ChapterReader: React.FC<ChapterReaderProps> = ({ className = '' }) => {
  const { chapterNumber } = useParams<{ chapterNumber: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [viewMode, setViewMode] = useState<ViewMode>('scroll');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [fontSize, setFontSize] = useState<number>(16);
  const [lineHeight, setLineHeight] = useState<number>(1.6);
  const [topDonors, setTopDonors] = useState<FormattedDonor[]>([]);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [accessLoading, setAccessLoading] = useState<boolean>(true);
  const [accessError, setAccessError] = useState<string>('');

  // Chapter content data (in real app, this would come from API)
  const chapterData: ChapterData = {
    1: {
      title: "The Shattered Moon",
      content: [
        "In the ghostly city of Caer Requiem, where the broken moon cast its fractured light through eternal twilight, Liora awakened to the sound of whispers that weren't there.",
        "The ancient cobblestones beneath her feet seemed to pulse with a rhythm she couldn't understand, while shadows danced at the edges of her vision like memories trying to break free.",
        "She had no past, no name beyond what the city's few remaining inhabitants had given her, and no future she could imagine. But the dreams... the dreams spoke of Lunaria.",
        "Every night, visions of a world where the moon was whole filled her sleeping mind. Cities of crystal and starlight, gardens that bloomed with celestial fire, and a people who lived in harmony with the cosmos itself.",
        "But those were just dreams. This broken world of twisted magic and fading hope was all she had ever known. Or so she had been told.",
        "As she walked through the ruins of what had once been a magnificent library, her fingers traced the spines of books written in languages that no longer existed. Yet somehow, impossibly, she could read them.",
        "The texts spoke of the Convergence, the moment when Lunaria and Earth had been one realm, and of the Shattering that had torn them apart. They spoke of guardians who could walk between worlds, and of a prophecy written in starlight.",
        "Liora's hand moved to the pendant at her throat, a shard of moonstone that had been with her for as long as she could remember. As her fingers closed around it, the whispers grew louder.",
        "They were calling her name. Her real name. A name that belonged to a girl who had lived in the time before the Shattering, when the moon was whole and magic flowed like rivers of light through both worlds.",
        "The pendant grew warm, then hot, and suddenly the library around her began to shift and change. The walls became transparent, showing glimpses of another place, another time. Lunaria was bleeding through.",
        "And in that moment of cosmic revelation, Liora realized that her journey was just beginning. The broken moon above was not an ending, but a promise. A promise that what was shattered could be made whole again.",
        "But first, she would have to remember who she truly was. And that meant facing the truth about the night the worlds were torn apart."
      ]
    },
    2: {
      title: "Echoes of Memory",
      content: [
        "Three days had passed since the vision in the library, and Liora found herself drawn deeper into the ruins of Caer Requiem.",
        "The city seemed to respond to her presence now, as if awakening from a long slumber. Streets that had been blocked by rubble mysteriously cleared overnight.",
        "In the marketplace that had stood empty for centuries, she began to hear the echoes of voices from the past. Merchants hawking their wares, children laughing, lovers whispering sweet promises.",
        "But it was in the old cathedral that she made her most disturbing discovery. Behind the altar, hidden beneath layers of dust and time, she found a mural that depicted the Shattering.",
        "The artwork showed two worlds in perfect harmony, connected by bridges of starlight. Lunaria hung in the sky above Earth like a second moon, its crystal cities gleaming with inner fire.",
        "And there, in the center of the mural, was a figure that looked exactly like her. The Guardian of the Bridge, the caption read in the ancient script she somehow understood.",
        "According to the mural, the Guardian was the one who maintained the connection between worlds. But she was also the one who could sever it, if the balance was threatened.",
        "The implications hit Liora like a physical blow. Had she been responsible for the Shattering? Had she been the one to break the moon and condemn both worlds to slow decay?",
        "As if summoned by her thoughts, a figure emerged from the shadows of the cathedral. An old man with eyes like starlight and robes that seemed to be cut from the fabric of night itself.",
        "'You're beginning to remember,' he said, his voice carrying the weight of centuries. 'I wondered when you would find this place.'",
        "'Who are you?' Liora whispered, though part of her already knew the answer.",
        "'I am Erasmus, the Last Keeper of Lunaria. And you, child, are the key to healing what was broken. But the path ahead is darker than you can imagine.'"
      ]
    },
    3: {
      title: "The Last Keeper",
      content: [
        "Erasmus led her through passages that didn't exist on any map, through doors that opened onto impossible vistas.",
        "They walked through memories made manifest, past echoes of the world that had been lost when the Shattering tore reality apart.",
        "In his hidden sanctuary beneath the city, surrounded by artifacts from both worlds, the old Keeper told her the truth.",
        "The Shattering had not been an accident. It had been a desperate choice, made by the Guardian who came before her when darkness threatened to consume both worlds.",
        "But the cure had been worse than the disease. The separation had saved both worlds from destruction, but it had also doomed them to a slow, inexorable decline.",
        "Earth grew gray and lifeless without the magic of Lunaria, while Lunaria became a realm of endless twilight without the grounding force of Earth.",
        "Both worlds were dying, and only the Guardian could heal what had been broken.",
        "But the ritual would require a sacrifice beyond imagination, and the Guardian would have to choose which world would survive.",
        "As the weight of destiny settled on her shoulders, Liora realized that the whispers she had been hearing weren't random voices from the past.",
        "They were the voices of all the Guardians who had come before, offering their wisdom and their strength for the trials ahead.",
        "The portal to Lunaria was opening, and her true journey was about to begin."
      ]
    },
    4: {
      title: "Bridge Between Worlds",
      content: [
        "The portal opened like a wound in reality, bleeding light and shadow in equal measure.",
        "Stepping through felt like being born again, every sense overwhelmed by the alien beauty of Lunaria.",
        "Here, the moon was whole and hung low in the sky, its silver light transforming everything it touched.",
        "The cities were alive, their crystal towers humming with harmonious songs that resonated in her bones.",
        "But even in this realm of wonder, she could see the signs of decay. The light was dimming, the songs growing fainter.",
        "The inhabitants of Lunaria, beings of starlight and dream, greeted her with desperate hope.",
        "They had been waiting for the Guardian to return, to restore the bridge between worlds.",
        "But Liora was not ready. She was still learning to control her power, still discovering the true extent of her abilities.",
        "And in the distance, she could sense a growing darkness, a force that had been waiting for her return.",
        "The real test was yet to come."
      ]
    },
    // Add more chapters as needed
  };

  const currentChapter = chapterData[parseInt(chapterNumber || '1')] || chapterData[1];
  const totalPages = currentChapter.content.length;

  // SECURE: Verify access on component mount
  useEffect(() => {
    const verifyAccess = async () => {
      if (!user || authLoading) return;

      try {
        setAccessLoading(true);
        const chapterId = parseInt(chapterNumber || '1');
        
        // SECURE: Always verify access from server
        const accessVerification = await chapterService.verifyChapterAccess(user.uid, chapterId);
        
        if (accessVerification.hasAccess) {
          setHasAccess(true);
          setAccessError('');
        } else {
          setHasAccess(false);
          setAccessError(accessVerification.reason);
        }
      } catch (error) {
        console.error('Error verifying chapter access:', error);
        setHasAccess(false);
        setAccessError('Unable to verify access. Please try again.');
      } finally {
        setAccessLoading(false);
      }
    };

    verifyAccess();
  }, [user, authLoading, chapterNumber]);

  // Copy protection functions
  const disableTextSelection = useCallback((): void => {
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    (document.body.style as any).mozUserSelect = 'none';
    (document.body.style as any).msUserSelect = 'none';
    (document.body.style as any).webkitUserDrag = 'none';
    (document.body.style as any).mozUserDrag = 'none';
  }, []);

  const enableTextSelection = useCallback((): void => {
    document.body.style.userSelect = 'auto';
    document.body.style.webkitUserSelect = 'auto';
    (document.body.style as any).mozUserSelect = 'auto';
    (document.body.style as any).msUserSelect = 'auto';
    (document.body.style as any).webkitUserDrag = 'auto';
    (document.body.style as any).mozUserDrag = 'auto';
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent): void => {
    // Disable common copy/save shortcuts
    if (
      (e.ctrlKey || e.metaKey) && 
      (e.key === 'c' || e.key === 'C' || 
       e.key === 'a' || e.key === 'A' || 
       e.key === 's' || e.key === 'S' ||
       e.key === 'p' || e.key === 'P' ||
       e.key === 'u' || e.key === 'U')
    ) {
      e.preventDefault();
      return;
    }
    
    // Disable F12 and other developer shortcuts
    if (
      e.key === 'F12' || 
      (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) ||
      (e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j')) ||
      (e.ctrlKey && (e.key === 'U' || e.key === 'u'))
    ) {
      e.preventDefault();
      return;
    }
  }, []);

  const handleContextMenu = useCallback((e: MouseEvent): void => {
    e.preventDefault();
  }, []);

  useEffect(() => {
    // Only apply protections and load content if user has access
    if (!hasAccess || accessLoading) return;

    // Load reading progress
    const savedPage: string | null = localStorage.getItem(`lunaria-chapter-${chapterNumber}-page`);
    if (savedPage) {
      setCurrentPage(parseInt(savedPage));
    }

    // Apply copy protection
    disableTextSelection();
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    
    // Disable print
    window.addEventListener('beforeprint', (e) => {
      e.preventDefault();
      alert('Printing is disabled for copyright protection.');
    });

    // Load top donors for previous chapter from Firebase
    const loadTopDonors = async (): Promise<void> => {
      try {
        const chapterId = parseInt(chapterNumber || '1');
        const donors = await donationService.getTopDonors(chapterId);
        
        // Transform Firebase response to match component format
        const formattedDonors: FormattedDonor[] = donors.map((donor: any) => ({
          name: donor.userName,
          amount: donor.amount,
          date: donor.donationDate
        }));
        setTopDonors(formattedDonors);
      } catch (error) {
        console.error('Error loading top donors:', error);
        // Fallback to demo data
        setTopDonors([
          { name: 'Alex R.', amount: 500, date: '2024-12-01' },
          { name: 'Sarah M.', amount: 350, date: '2024-12-02' },
          { name: 'Jordan K.', amount: 250, date: '2024-12-01' },
          { name: 'Emily C.', amount: 200, date: '2024-12-03' },
          { name: 'Michael D.', amount: 150, date: '2024-12-02' },
          { name: 'Anna L.', amount: 100, date: '2024-12-04' },
          { name: 'David P.', amount: 100, date: '2024-12-01' },
          { name: 'Lisa W.', amount: 75, date: '2024-12-03' },
          { name: 'Ryan T.', amount: 50, date: '2024-12-02' },
          { name: 'Maya S.', amount: 50, date: '2024-12-04' }
        ]);
      }
    };

    loadTopDonors();

    // Cleanup function
    return () => {
      enableTextSelection();
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [chapterNumber, disableTextSelection, enableTextSelection, handleKeyDown, handleContextMenu, hasAccess, accessLoading]);

  const saveProgress = (page: number): void => {
    localStorage.setItem(`lunaria-chapter-${chapterNumber}-page`, page.toString());
  };

  const handleNextPage = (): void => {
    if (currentPage < totalPages - 1) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      saveProgress(newPage);
    }
  };

  const handlePrevPage = (): void => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      saveProgress(newPage);
    }
  };

  const handleViewModeChange = (mode: ViewMode): void => {
    setViewMode(mode);
    setShowSettings(false);
  };

  const handleDonation = async (): Promise<void> => {
    // Donation functionality has been disabled
    alert('Donation functionality is currently disabled');
  };

  // Loading state
  if (authLoading || accessLoading) {
    return (
      <div className={`chapter-reader ${className}`}>
        <Navbar />
        <div className="reader-container">
          <div className="access-loading">
            <div className="spinner"></div>
            <p>Loading chapter...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Access denied state
  if (!hasAccess) {
    return (
      <div className={`chapter-reader ${className}`}>
        <Navbar />
        <div className="reader-container">
          <div className="access-denied">
            <Lock size={48} />
            <h2>Chapter Access Restricted</h2>
            <p>{accessError}</p>
            <div className="access-actions">
              <button onClick={() => navigate('/chapters')} className="back-btn">
                Back to Chapters
              </button>
              {!user && (
                <button onClick={() => navigate('/chapters')} className="signin-btn">
                  Sign In to Continue
                </button>
              )}
              {user && accessError === 'Chapter not purchased' && (
                <button onClick={() => navigate('/chapters')} className="purchase-btn">
                  Purchase Chapter
                </button>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const renderScrollView = () => (
    <div className="scroll-view no-select" style={{ fontSize: `${fontSize}px`, lineHeight }}>
      <div className="chapter-header">
        <h1>Chapter {chapterNumber}: {currentChapter.title}</h1>
      </div>
      
      <div className="chapter-content">
        {currentChapter.content.map((paragraph: string, index: number) => (
          <motion.p
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="chapter-paragraph no-select"
          >
            {paragraph}
          </motion.p>
        ))}
      </div>

      {/* Support Author Section */}
      <div className="support-section">
        <h3><Heart className="icon" /> Support the Author</h3>
        <p>If you enjoyed this chapter, consider supporting the author with a donation!</p>
        <button onClick={handleDonation} className="donation-btn">
          <Heart size={16} />
          Donate Now
        </button>
      </div>

      {/* Top Donors Section */}
      <div className="top-donors-section">
        <h3><Trophy className="icon" /> Top 10 Supporters of Chapter {parseInt(chapterNumber || '1') - 1}</h3>
        <p className="donors-description">
          These amazing readers supported the author when Chapter {parseInt(chapterNumber || '1') - 1} was released!
        </p>
        <div className="donors-grid">
          {topDonors.map((donor: FormattedDonor, index: number) => (
            <motion.div
              key={index}
              className={`donor-card ${index < 3 ? 'top-three' : ''}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <div className="donor-rank">#{index + 1}</div>
              <div className="donor-info">
                <div className="donor-name">{donor.name}</div>
                <div className="donor-amount">₹{donor.amount}</div>
                <div className="donor-date">{donor.date}</div>
              </div>
              {index < 3 && <Heart className="top-donor-icon" />}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderFlipView = () => (
    <div className="flip-view">
      <div className="book-container">
        <div className="page-counter">
          Page {currentPage + 1} of {totalPages}
        </div>
        
        <motion.div
          key={currentPage}
          initial={{ opacity: 0, rotateY: 90 }}
          animate={{ opacity: 1, rotateY: 0 }}
          exit={{ opacity: 0, rotateY: -90 }}
          transition={{ duration: 0.5 }}
          className="book-page no-select"
          style={{ fontSize: `${fontSize}px`, lineHeight }}
        >
          {currentPage === 0 && (
            <div className="chapter-title-page">
              <h1>Chapter {chapterNumber}</h1>
              <h2>{currentChapter.title}</h2>
            </div>
          )}
          
          <div className="page-content no-select">
            {currentChapter.content[currentPage] && (
              <p className="no-select">{currentChapter.content[currentPage]}</p>
            )}
          </div>
        </motion.div>

        <div className="flip-controls">
          <motion.button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flip-btn prev"
          >
            <ChevronLeft size={24} />
          </motion.button>
          
          <motion.button
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flip-btn next"
          >
            <ChevronRight size={24} />
          </motion.button>
        </div>

        {/* Show top donors on last page */}
        {currentPage === totalPages - 1 && (
          <div className="flip-donors-section">
            <h4><Trophy size={16} /> Top Supporters</h4>
            <button onClick={handleDonation} className="flip-donation-btn">
              <Heart size={14} />
              Support Author
            </button>
            <div className="flip-donors-list">
              {topDonors.slice(0, 5).map((donor: FormattedDonor, index: number) => (
                <div key={index} className="flip-donor-item">
                  #{index + 1} {donor.name} - ₹{donor.amount}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`chapter-reader ${className}`}>
      <Navbar />
      
      {/* Enhanced Fantasy Background with Multiple Layers */}
      <div className="ambient-glow"></div>
      
      {/* Enhanced Magical Particles */}
      <div className="floating-particles">
        {/* Small particles */}
        {[...Array(8)].map((_, i) => (
          <div key={`small-${i}`} className={`particle small particle-${i}`}></div>
        ))}
        
        {/* Medium particles */}
        {[...Array(5)].map((_, i) => (
          <div key={`medium-${i}`} className={`particle medium particle-${i + 8}`}></div>
        ))}
        
        {/* Large particles */}
        {[...Array(3)].map((_, i) => (
          <div key={`large-${i}`} className={`particle large particle-${i + 13}`}></div>
        ))}
        
        {/* Magical Runes */}
        {['ᚱ', 'ᛟ', 'ᚺ', 'ᛗ'].map((rune, i) => (
          <div key={`rune-${i}`} className={`magical-rune rune-${i}`}>{rune}</div>
        ))}
        
        {/* Floating Stars */}
        {[...Array(5)].map((_, i) => (
          <div key={`star-${i}`} className={`floating-star star-${i}`}></div>
        ))}
        
        {/* Mystical Symbols */}
        {['◊', '◈', '◉'].map((symbol, i) => (
          <div key={`symbol-${i}`} className={`mystical-symbol symbol-${i}`}>{symbol}</div>
        ))}
      </div>
      
      {/* Reader Controls */}
      <div className="reader-controls">
        <div className="control-left">
          <motion.button
            onClick={() => navigate('/chapters')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="control-btn"
          >
            <ArrowLeft size={20} />
            Back to Chapters
          </motion.button>
        </div>

        <div className="control-center">
          <div className="view-mode-toggle">
            <motion.button
              onClick={() => handleViewModeChange('scroll')}
              className={`mode-btn ${viewMode === 'scroll' ? 'active' : ''}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Scroll size={18} />
              Scroll
            </motion.button>
            <motion.button
              onClick={() => handleViewModeChange('flip')}
              className={`mode-btn ${viewMode === 'flip' ? 'active' : ''}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <BookOpen size={18} />
              Flip
            </motion.button>
          </div>
        </div>

        <div className="control-right">
          <motion.button
            onClick={() => navigate('/wallet')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="manage-wallet-btn"
          >
            <Wallet size={20} />
            Manage Wallet
          </motion.button>
          <motion.button
            onClick={() => setShowSettings(!showSettings)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="control-btn"
          >
            <Settings size={20} />
            Settings
          </motion.button>
        </div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="settings-panel"
          >
            <div className="setting-group">
              <label>Font Size</label>
              <input
                type="range"
                min="12"
                max="24"
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
              />
              <span>{fontSize}px</span>
            </div>
            <div className="setting-group">
              <label>Line Height</label>
              <input
                type="range"
                min="1.2"
                max="2.0"
                step="0.1"
                value={lineHeight}
                onChange={(e) => setLineHeight(parseFloat(e.target.value))}
              />
              <span>{lineHeight}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="reader-main">
        {viewMode === 'scroll' ? renderScrollView() : renderFlipView()}
      </main>

      <Footer />
    </div>
  );
};

export default ChapterReader;